/**
 * Single source of truth for the static assets the editor-v2 canvas iframe
 * needs (KEditor/Froala CSS+JS and template-specific CSS/JS).
 *
 * Imported by BOTH sides of the iframe boundary:
 *  - src/components/helper/KEditor.vue (Vue 2 parent, webpack) builds the
 *    URL lists sent via INIT_EDITOR from these builders.
 *  - src/editor-v2/main.ts (Vue 3 iframe, vite) injects <link rel="preload">
 *    for the same URLs at boot, so downloads overlap the app boot and the
 *    INIT_EDITOR handshake instead of starting after it.
 *
 * Plain JS (no TS) so the Vue 2 webpack build can import it, like
 * src/editor-v2/utils/layoutWrapper.js.
 */

export const KEDITOR_SHARED_CSS_PATHS = [
  '/wps/_keditor_shared/spectrum.css',
  '/wps/_keditor_shared/selectize.css',
  '/wps/_keditor_shared/css/keditor-1.1.5.min.css',
  '/wps/_keditor_shared/css/keditor_custom.css',
  '/wps/_keditor_shared/plugins/simplebar/simplebar.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/froala_editor.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/froala_style.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/colors.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/table.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/emoticons.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/special_characters.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/help.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/image.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/video.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/code_view.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/plugins/draggable.min.css',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/css/third_party/embedly.min.css',
  '/wps/_keditor_shared/plugins/froala-oembed-main/src/froala-oembed.css',
  '/wps/_keditor_shared/plugins/bootstrap-iconpicker-1.10.0/dist/css/bootstrap-iconpicker.min.css',
  '/wps/_keditor_shared/plugins/rangeslider/rangeslider.css',
  '/wps/_keditor_shared/plugins/xncolorpicker/xncolorpicker.css',
];

// Ordered by dependency: jQuery first, Froala core second, then plugins + utilities.
export const KEDITOR_SHARED_JS_PATHS = [
  '/wps/_keditor_shared/plugins/jquery-1.11.3/jquery-1.11.3.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/froala_editor.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/align.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/colors.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/emoticons.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/font_family.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/font_size.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/help.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/image.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/inline_class.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/inline_style.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/line_breaker.min.js',
  '/wps/_keditor_shared/plugins/custom_froala/line_height.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/link.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/lists.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/paragraph_format.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/paragraph_style.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/special_characters.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/table.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/url.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/video.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/code_view.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/plugins/word_paste.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/third_party/embedly.min.js',
  '/wps/_keditor_shared/plugins/froala-oembed-main/src/froala-oembed.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/third_party/font_awesome.min.js',
  '/wps/_keditor_shared/plugins/wysiwyg-editor-5.0.0/js/languages/de.js',
  '/wps/_keditor_shared/js/froala/froala-config.js',
  '/wps/_keditor_shared/js/froala/froala-plugins-shared.js',
  '/wps/_keditor_shared/plugins/xncolorpicker/dist/xncolorpicker.min.js',
];

export function buildKeditorSharedCssUrls(base) {
  return KEDITOR_SHARED_CSS_PATHS.map((path) => `${base}${path}`);
}

export function buildKeditorSharedJsUrls(base) {
  return KEDITOR_SHARED_JS_PATHS.map((path) => `${base}${path}`);
}

/**
 * Appends the module cache-busting key. The PHP asset server hands these files
 * out with `max-age=31536000` and their names carry no hash, so without a
 * query an editor with a warm cache keeps last month's template CSS/JS.
 * Published magazines have always done this (lib.file.js appends `?v=`); the
 * editor did not.
 *
 * No version means no query — an old parent or the standalone canvas then
 * behaves exactly as before instead of requesting `?v=undefined`. The version
 * itself lives in src/utils/moduleAssetVersion.js and is passed in, so this
 * manifest stays free of imports and works in both the Vite and webpack build.
 */
function versioned(url, version) {
  if (!version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}`;
}

/**
 * Template/collection/theme specific CSS, served by the PHP asset server.
 */
export function buildTemplateCssUrls(phpBase, collection, templateName, theme, version) {
  const urls = [
    `${phpBase}/wps/_modules/templates/${collection}/${collection}.global.css`,
    `${phpBase}/assets/css/wm-tokens.css`,
    `${phpBase}/assets/css/snippets-tailwind.css`,
    `${phpBase}/wps/_modules/templates/${collection}/${templateName}/style.css`,
  ];
  if (theme) {
    urls.push(`${phpBase}/wps/_modules/themes/${theme}/direct.min.css`);
  }
  return urls.map((url) => versioned(url, version));
}

/**
 * Template/collection/theme specific JS, served by the PHP asset server.
 */
export function buildTemplateJsUrls(phpBase, collection, templateName, theme, version) {
  const urls = [
    `${phpBase}/wps/_modules/templates/${collection}/${collection}.global.js`,
    `${phpBase}/wps/_modules/templates/${collection}/${templateName}/index.js`,
  ];
  if (theme) {
    urls.push(`${phpBase}/wps/_modules/themes/${theme}/direct.min.js`);
  }
  return urls.map((url) => versioned(url, version));
}
